import { useState, useEffect, useCallback } from 'react';
import { Button, Chip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { Bot as AgentIcon, Plus as AddIcon, Trash2 as DeleteIcon } from 'lucide-react';
import { agentAttachmentService } from '../../services/agentAttachmentService';
import { AttachAgentDialog } from './AttachAgentDialog';
import type { AgentAttachmentDto } from '../../types/agentAttachment';

interface AttachedAgentsPanelProps {
  parentAgentId: string;
  'data-testid'?: string;
}

export const AttachedAgentsPanel = ({
  parentAgentId,
  'data-testid': testId = 'attached-agents-panel',
}: AttachedAgentsPanelProps) => {
  const [attachments, setAttachments] = useState<AgentAttachmentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await agentAttachmentService.getAttachments(parentAgentId);
      setAttachments(data);
    } finally {
      setLoading(false);
    }
  }, [parentAgentId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAttach = async (childId: string, alias?: string, descriptionOverride?: string) => {
    await agentAttachmentService.attach(parentAgentId, {
      childAgentDefinitionId: childId,
      alias,
      descriptionOverride,
      sortOrder: attachments.length,
    });
    load();
  };

  const handleDetach = async (attachmentId: string) => {
    await agentAttachmentService.detach(parentAgentId, attachmentId);
    load();
  };

  const excludeIds = [parentAgentId, ...attachments.map((a) => a.childAgentDefinitionId)];

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid={testId}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h6 className="text-[15px] font-semibold text-[var(--color-text-primary)]">Attached Sub-Agents</h6>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={() => setDialogOpen(true)}
          data-testid={`${testId}-attach-btn`}
        >
          <AddIcon size={14} aria-hidden />
          Attach
        </Button>
      </div>

      {loading && (
        <div
          className="mb-2 h-5 w-5 animate-spin rounded-full border-2 border-[var(--color-accent-primary)] border-t-transparent"
          role="status"
          aria-label="Loading attachments"
        />
      )}

      <div className="min-h-0 flex-1 space-y-1.5 overflow-auto">
        {attachments.map((att) => (
          <div
            key={att.id}
            className="flex items-start gap-2 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-2.5 py-2"
            data-testid={`${testId}-row-${att.id}`}
          >
            <span
              className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-subtle)] text-[var(--color-accent-primary)]"
              aria-hidden
            >
              <AgentIcon size={14} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-[var(--color-text-primary)]">
                {att.alias || att.childAgentName}
              </p>
              <p className="truncate text-[11px] text-[var(--color-text-secondary)]">{att.childAgentSlug}</p>
              {!att.isEnabled && (
                <Chip label="Disabled" className="mt-1 text-[10px]" variant="outlined" />
              )}
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => handleDetach(att.id)}
                    className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-inset)] hover:text-[var(--color-danger,var(--color-text-primary))]"
                    aria-label={`Detach ${att.alias || att.childAgentName}`}
                    data-testid={`${testId}-detach-${att.id}`}
                  >
                    <DeleteIcon size={14} />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Detach</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ))}

        {attachments.length === 0 && !loading && (
          <div
            className="rounded-lg border border-dashed border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-4 text-center"
            data-testid={`${testId}-empty`}
          >
            <p className="text-xs text-[var(--color-text-secondary)]">
              No sub-agents attached yet. Click <span className="font-medium text-[var(--color-text-primary)]">Attach</span> to
              add agents the orchestrator can call as tools.
            </p>
          </div>
        )}
      </div>

      <p className="mt-3 border-t border-[var(--color-border-default)] pt-3 text-[11px] leading-relaxed text-[var(--color-text-secondary)]">
        Attached agents appear as callable tools to the orchestrator LLM. It can invoke them via tool calls to delegate
        sub-tasks.
      </p>

      <AttachAgentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAttach={handleAttach}
        excludeIds={excludeIds}
      />
    </div>
  );
};
